import { z } from 'zod';

/**
 * Rolling window (in days) the admin "Recent activity" panel summarizes.
 *
 * The panel is a coarse "is this org actually using the product?" signal for
 * follow-up on freshly onboarded orgs — not a detailed audit — so the window
 * is fixed rather than caller-tunable. Shared so the server clamp and the web
 * copy ("Past 7 days") stay in agreement.
 */
export const ORG_ACTIVITY_WINDOW_DAYS = 7;

/**
 * A single at-a-glance activity counter for an organization.
 *
 * Metrics are contributed by both the host (agent sessions, sign-ins) and
 * plugins (personas, flows) through the server-side org-activity registry, so
 * the set of metrics an org sees depends on which plugins are installed. The
 * web panel renders each as a labelled count tile and never assumes a fixed
 * roster — a new metric source needs no web change.
 */
export const OrgActivityMetricSchema = z
  .object({
    /** Stable, unique identifier (e.g. `core.agent_sessions`, `flows.created`). */
    key: z.string(),
    /** Short human label, e.g. "Agent sessions". */
    label: z.string(),
    /** Count over the window. Always a non-negative integer. */
    count: z.number().int().nonnegative(),
    /**
     * Optional lucide icon name hint (e.g. `Bot`). The panel maps known names
     * to icons and falls back to a generic activity glyph for anything else,
     * so an unknown or omitted value renders fine.
     */
    icon: z.string().optional(),
    /** Optional one-line subtitle shown under the count. */
    description: z.string().optional(),
  })
  .meta({ id: 'OrgActivityMetric' });
export type OrgActivityMetric = z.infer<typeof OrgActivityMetricSchema>;

/**
 * Response for `GET /admin/organizations/:organizationId/activity`.
 *
 * `since`/`generatedAt` are epoch-millisecond timestamps (standard `Date`
 * API), matching the rest of the shared contract.
 */
export const OrgActivitySummarySchema = z
  .object({
    organizationId: z.string(),
    /** Length of the summarized window in days (see {@link ORG_ACTIVITY_WINDOW_DAYS}). */
    windowDays: z.number().int().positive(),
    /** Start of the window, epoch ms. Rows with a timestamp >= this are counted. */
    since: z.number(),
    /** When the summary was computed, epoch ms. */
    generatedAt: z.number(),
    /** Ordered metric tiles, most prominent first. */
    metrics: z.array(OrgActivityMetricSchema),
  })
  .meta({ id: 'OrgActivitySummary' });
export type OrgActivitySummary = z.infer<typeof OrgActivitySummarySchema>;
